import { Alert, Box, Paper, Typography } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import LogoLoader from "../common/LogoLoader";
import { useAuth } from "../../contexts/AuthContext";
import { useGraphData } from "../../hooks/queries/useGraphData";
import EdgeDetailOverlay from "../Layout/EdgeDetailOverlay";
import EntityDetailOverlay from "../Layout/EntityDetailOverlay";
import { CompanyOption } from "../Layout/filters/VirtualizedCompanyDropdown";
import { GraphCanvas, GraphLink, GraphNode } from "./GraphCanvas";

interface GraphParameters {
  entityId: string;
  selectedCompany: CompanyOption | null;
  orgStructure: string;
  supplierTiers: number;
  customerTiers: number;
  layoutType: "force" | "hierarchy";
  showEdgeLabels: boolean;
  showNodeLabels: boolean;
}

interface GraphContainerProps {
  graphParameters: GraphParameters;
  onLoadingChange?: (loading: boolean) => void;
  onGraphDataLoaded?: (fetchedEntityId: string) => void;
  canvasRef?: React.MutableRefObject<any>;
  refreshTriggerRef?: React.MutableRefObject<(() => void) | null>;
  filters?: {
    homeCountries?: string[];
    organizationTypes?: string[];
    naicsCodes?: string[];
    inputHSCodes?: string[];
    outputHSCodes?: string[];
    foundingYearMin?: string;
    foundingYearMax?: string;
    labels?: string[];
    relationshipFilters?: any[];
  };
}

export const GraphContainer: React.FC<GraphContainerProps> = ({
  graphParameters,
  onLoadingChange,
  onGraphDataLoaded,
  canvasRef: externalCanvasRef,
  refreshTriggerRef,
  filters,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailEntityId, setDetailEntityId] = useState<string | null>(null);
  const [detailRelationshipId, setDetailRelationshipId] = useState<
    string | null
  >(null);
  // Override entity ID for node right-click navigation
  const [overrideEntityId, setOverrideEntityId] = useState<string | undefined>(
    undefined,
  );

  // Use parameters from props
  const parameters = graphParameters;
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  // Use external canvas ref if provided, otherwise use internal
  const internalCanvasRef = useRef<any>();
  const canvasRef = externalCanvasRef || internalCanvasRef;

  // Build live graph options from parameters + filters (changes on every tweak)
  const liveGraphOptions = useMemo(
    () => ({
      orgStructure: parameters.orgStructure
        ? parameters.orgStructure
        : undefined,
      supplierTiers:
        parameters.supplierTiers > 0 ? parameters.supplierTiers : undefined,
      customerTiers:
        parameters.customerTiers > 0 ? parameters.customerTiers : undefined,
      homeCountry: filters?.homeCountries,
      organizationTypes: filters?.organizationTypes,
      naics: filters?.naicsCodes,
      inputHsCodes: filters?.inputHSCodes,
      outputHsCodes: filters?.outputHSCodes,
      foundingYearMin: filters?.foundingYearMin
        ? parseInt(filters.foundingYearMin)
        : undefined,
      foundingYearMax: filters?.foundingYearMax
        ? parseInt(filters.foundingYearMax)
        : undefined,
      labels: filters?.labels,
      relationshipFilters: filters?.relationshipFilters,
    }),
    [parameters, filters],
  );

  // Resolve the effective entity ID (override from node right-click, or prop)
  const liveEntityId = overrideEntityId || parameters.entityId;

  // Committed options: only updated when the user clicks "Update Graph" or on
  // initial load.  The query key uses these so old data stays visible while
  // the user tweaks settings.
  const [committedEntityId, setCommittedEntityId] = useState(liveEntityId);
  const [committedOptions, setCommittedOptions] = useState(liveGraphOptions);

  // Fetch graph data via TanStack Query (manual fetch only — no auto-refetch)
  const graphQuery = useGraphData(committedEntityId, committedOptions);

  const graphData = graphQuery.data ?? { nodes: [], links: [] };
  const loading = graphQuery.isLoading || graphQuery.isFetching;
  const error = graphQuery.error
    ? (graphQuery.error as any).response?.data?.error ||
      (graphQuery.error as Error).message ||
      "Failed to fetch graph data"
    : null;

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(loading);
    }
  }, [loading, onLoadingChange]);

  // Initial fetch when component mounts with an entity, or when
  // overrideEntityId changes (node right-click navigation)
  useEffect(() => {
    if (liveEntityId) {
      setCommittedEntityId(liveEntityId);
      setCommittedOptions(liveGraphOptions);
    }
    // Only trigger on entity ID changes, not on every option change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveEntityId]);

  // Notify parent when graph data is loaded
  useEffect(() => {
    if (graphQuery.data && committedEntityId) {
      onGraphDataLoaded?.(committedEntityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphQuery.data, committedEntityId]);

  // Update dimensions when container size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    // Initial size
    updateDimensions();

    // Update on window resize
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  const handleRefresh = useCallback(() => {
    // Commit current live options so the query key updates and fetches
    setOverrideEntityId(undefined);
    const entityId = parameters.entityId;
    setCommittedEntityId(entityId);
    setCommittedOptions(liveGraphOptions);
  }, [parameters.entityId, liveGraphOptions]);

  // Store refresh function in ref for external triggering
  useEffect(() => {
    if (refreshTriggerRef) {
      refreshTriggerRef.current = handleRefresh;
    }
  }, [handleRefresh, refreshTriggerRef]);

  // handleNodeClick removed - GraphContainer manages its own entity selection via URL

  const handleZoomIn = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.zoom(canvasRef.current.zoom() * 1.2);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.zoom(canvasRef.current.zoom() * 0.8);
    }
  }, []);

  const handleCenter = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.zoomToFit(400);
    }
  }, []);

  const handleReset = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.d3ReheatSimulation();
    }
  }, []);

  // Graph node selection handlers (for highlighting)
  const handleEntityClick = useCallback(
    (node: GraphNode) => {
      // Open detail overlay for the clicked node
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("detail_entity_id", node.id);
          // Close edge detail if open (mutual exclusivity)
          params.delete("detail_relationship_id");
          return params;
        },
        { replace: false },
      ); // Don't replace - create history entry
    },
    [setSearchParams],
  );

  // Graph edge selection handler
  const handleEdgeClick = useCallback(
    (edge: GraphLink) => {
      // Open detail overlay for the clicked edge
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("detail_relationship_id", edge.id);
          // Close entity detail if open (mutual exclusivity)
          params.delete("detail_entity_id");
          return params;
        },
        { replace: false },
      ); // Don't replace - create history entry
    },
    [setSearchParams],
  );

  // Detail overlay handlers
  const handleCloseEntityDetail = useCallback(() => {
    setDetailEntityId(null);
    setSearchParams(
      (prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete("detail_entity_id");
        return newParams;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const handleCloseEdgeDetail = useCallback(() => {
    setDetailRelationshipId(null);
    setSearchParams(
      (prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete("detail_relationship_id");
        return newParams;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const handleBackgroundClick = useCallback(() => {
    setSearchParams(
      (prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete("detail_entity_id");
        newParams.delete("detail_relationship_id");
        return newParams;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const handleEntityNavigation = useCallback(
    (entityId: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("detail_entity_id", entityId);
          params.delete("detail_relationship_id");
          return params;
        },
        { replace: false },
      ); // Don't replace - create history entry
    },
    [setSearchParams],
  );

  const handleNodeRightClick = useCallback(
    (node: any) => {
      if (node.id) {
        // Update URL with new seed entity - this will trigger GraphView to update parameters
        setSearchParams(
          (prev) => {
            const params = new URLSearchParams(prev);
            params.set("seed_entity", node.id);
            return params;
          },
          { replace: false },
        );
        // Set override entity ID — TanStack Query will automatically fetch the new graph
        setOverrideEntityId(node.id);
      }
    },
    [setSearchParams],
  );

  // Read entity and relationship parameters from URL for detail overlays
  useEffect(() => {
    const entityId = searchParams.get("detail_entity_id");
    const relationshipId = searchParams.get("detail_relationship_id");
    setDetailEntityId(entityId);
    setDetailRelationshipId(relationshipId);
  }, [searchParams]);

  // Note: Removed URL -> parameters sync to prevent circular dependency
  // URL is updated by the parameters -> URL sync effect above

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "#262626",
      }}
    >
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <LogoLoader size="panel" surface="paper" label="Loading the graph" sx={{ mb: 2 }} />
            <Typography>Loading graph data...</Typography>
          </Paper>
        </Box>
      )}

      {/* Error Display */}
      {error && !loading && (
        <Alert
          severity="error"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            maxWidth: 400,
            zIndex: 1000,
          }}
          onClose={() => graphQuery.refetch()}
        >
          {error}
        </Alert>
      )}

      {/* Graph Canvas - Only render when data is loaded */}
      {!loading && graphData.nodes.length > 0 && (
        <GraphCanvas
          key={`graph-${parameters.entityId}-${graphData.nodes.length}`}
          data={graphData}
          onNodeClick={handleEntityClick}
          onNodeRightClick={handleNodeRightClick}
          onBackgroundClick={handleBackgroundClick}
          onEdgeClick={handleEdgeClick}
          selectedNodeId={undefined}
          seedEntityId={searchParams.get("seed_entity") || undefined}
          width={dimensions.width}
          height={dimensions.height}
          layoutType={parameters.layoutType}
          showEdgeLabels={parameters.showEdgeLabels}
          showNodeLabels={parameters.showNodeLabels}
        />
      )}

      {/* Empty State - Show when not loading and no data */}
      {!loading && graphData.nodes.length === 0 && !error && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          <Typography variant="h6" gutterBottom>
            No Graph Data
          </Typography>
          <Typography variant="body2">
            Enter a seed entity ID and click Refresh to load graph data
          </Typography>
        </Box>
      )}

      {/* Graph Controls */}
      {/* <GraphControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onCenter={handleCenter}
        onReset={handleReset}
      /> */}

      {/* Graph Legend */}
      {/* <GraphLegend /> */}

      {/* Entity Detail Overlay */}
      {detailEntityId && (
        <EntityDetailOverlay
          entityId={detailEntityId}
          onClose={handleCloseEntityDetail}
          onEntityClick={handleEntityNavigation}
        />
      )}

      {/* Edge Detail Overlay */}
      {detailRelationshipId && (
        <EdgeDetailOverlay
          relationshipId={detailRelationshipId}
          onClose={handleCloseEdgeDetail}
          onEntityClick={handleEntityNavigation}
        />
      )}
    </Box>
  );
};
